/**
 * creators — Orbit's own creator/beta roster (admin surface).
 *
 * DEMO-4: `mockCreators` had every joinedDate in January 2024, which
 * looked absurd beside the rest of a 2026 demo. Replaced by
 * `demoCreators`, whose join dates are computed relative to now.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { demoCreators } from '@/data/demoOverlay';
import { Creator } from '@/types/crm';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type DBCreator = Tables<'creators'>;

// Map DB creator to UI Creator type
const mapDbToUi = (db: DBCreator): Creator => ({
  id: db.id,
  name: db.name,
  email: db.email,
  role: db.role,
  status: db.status,
  plan: db.plan || 'beta',
  joinedDate: db.joined_date || db.created_at.split('T')[0],
  foundingMember: db.founding_member || false,
  notes: db.notes || undefined,
});

// Map UI Creator to DB update type
const mapUiToDbUpdate = (ui: Partial<Creator>): TablesUpdate<'creators'> => ({
  name: ui.name,
  email: ui.email,
  role: ui.role,
  status: ui.status,
  plan: ui.plan,
  joined_date: ui.joinedDate,
  founding_member: ui.foundingMember,
  notes: ui.notes || null,
});

export const useCreators = () => {
  const { isDemo } = useDemoMode();

  return useQuery({
    queryKey: ['creators', isDemo],
    queryFn: async (): Promise<Creator[]> => {
      if (isDemo) {
        return demoCreators as unknown as Creator[];
      }

      const { data, error } = await supabase
        .from('creators')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(mapDbToUi);
    },
  });
};

export const useCreateCreator = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();

  return useMutation({
    mutationFn: async (creator: TablesInsert<'creators'>) => {
      if (isDemo) {
        return { id: crypto.randomUUID(), ...creator };
      }

      const { data, error } = await supabase
        .from('creators')
        .insert(creator)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creators'] });
    },
  });
};

export const useUpdateCreator = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Creator> }) => {
      if (isDemo) {
        return { id, ...updates };
      }

      const dbUpdates = mapUiToDbUpdate(updates);
      const { data, error } = await supabase
        .from('creators')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creators'] });
    },
  });
};

export const useDeleteCreator = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();

  return useMutation({
    mutationFn: async (id: string) => {
      if (isDemo) {
        return { id };
      }

      const { error } = await supabase.from('creators').delete().eq('id', id);

      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creators'] });
    },
  });
};
